import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { User, MessageSquare, Crown } from "lucide-react";
import { useEffect } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";

export function DailyLimitsCard() {
  const { workspace } = useWorkspace();

  const { data: clients, refetch } = useQuery({
    queryKey: ["workspace-daily-limits", workspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select(`
          id, 
          email, 
          linkedin_name,
          status, 
          is_premium,
          today_connections_count, 
          today_messages_count,
          max_connections_per_day,
          max_messages_per_day
        `)
        .eq("workspace_id", workspace?.id || "")
        .in("status", ["active", "syncing"])
        .order("email");

      if (error) throw error;
      return data;
    },
    enabled: !!workspace?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Subscribe to real-time updates for client counters
  useEffect(() => {
    if (!workspace?.id) return;

    const channel = supabase
      .channel("daily-limits-updates")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "clients",
          filter: `workspace_id=eq.${workspace.id}`,
        },
        () => refetch()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [workspace?.id, refetch]);

  const getUsageColor = (pct: number) =>
    pct >= 90 ? "text-destructive" : pct >= 70 ? "text-amber-500" : "text-emerald-600";

  const getProgressColor = (pct: number) =>
    pct >= 90 ? "bg-destructive" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";

  const getStatusBadge = (pct: number) => {
    if (pct >= 90) return { variant: "destructive" as const, label: "Critical" };
    if (pct >= 70) return { variant: "secondary" as const, label: "High" };
    return { variant: "outline" as const, label: "Normal" };
  };

  const calcPct = (used: number, limit: number) =>
    limit === 0 ? 0 : Math.min((used / limit) * 100, 100);

  if (!clients || clients.length === 0) {
    return null;
  }

  return (
    <Card className="border-sidebar-border/60 shadow-sm bg-card/50 backdrop-blur-sm">
      <CardHeader className="pb-3 border-b border-sidebar-border/60">
        <CardTitle className="text-base flex items-center gap-2">
          <User className="w-4 h-4 text-primary" />
          Daily LinkedIn Limits
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {clients.map((client) => {
          const connLimit = client.is_premium ? 25 : 20;
          const msgLimit = client.is_premium ? 25 : 20;
          const connUsed = client.today_connections_count || 0;
          const msgUsed = client.today_messages_count || 0;
          const connPct = calcPct(connUsed, connLimit);
          const msgPct = calcPct(msgUsed, msgLimit);

          return (
            <div
              key={client.id}
              className="space-y-3 pb-4 border-b border-sidebar-border/40 last:border-0 last:pb-0"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium truncate">
                    {client.linkedin_name || client.email}
                  </span>
                  {client.is_premium && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 gap-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      <Crown className="w-3 h-3" />
                      Premium
                    </Badge>
                  )}
                </div>
                <Badge variant="outline" className="text-[10px] shrink-0">
                  {client.status}
                </Badge>
              </div>

              {/* Connections */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <User className="w-3 h-3" />
                    <span>Connections</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${getUsageColor(connPct)}`}>
                      {connUsed} / {connLimit}
                    </span>
                    <Badge
                      variant={getStatusBadge(connPct).variant}
                      className="text-[10px] px-1.5 py-0 h-4"
                    >
                      {getStatusBadge(connPct).label}
                    </Badge>
                  </div>
                </div>
                <div className="relative h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full transition-all ${getProgressColor(connPct)}`}
                    style={{ width: `${connPct}%` }}
                  />
                </div>
              </div>

              {/* Messages */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <MessageSquare className="w-3 h-3" />
                    <span>Messages</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${getUsageColor(msgPct)}`}>
                      {msgUsed} / {msgLimit}
                    </span>
                    <Badge
                      variant={getStatusBadge(msgPct).variant}
                      className="text-[10px] px-1.5 py-0 h-4"
                    >
                      {getStatusBadge(msgPct).label}
                    </Badge>
                  </div>
                </div>
                <div className="relative h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full transition-all ${getProgressColor(msgPct)}`}
                    style={{ width: `${msgPct}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
